/* eslint-disable no-shadow */

import { camelizeKeys } from 'humps';
import qs from 'qs';

import { NAVIGATION_METHODS } from '@ezugi/constants';
import { navigate, isUrl, isNil } from '@ezugi/utils';

import { postMessage, POST_MESSAGES } from '../../../util/postMessage';
import { AuthParams, CashierConfig } from '../../reducers/config';
import { UserState } from '../../reducers/user';

type LinkNavigationResult = {
  [key: string]: any;
};

type NavigationConfig = {
  method?: string;
  url?: string;
  width?: number;
  height?: number;
  outerLink?: string | boolean;
  queryString?: string;
};

const isPopup = () => !!window.opener;

/* eslint-disable camelcase */
const sendCashierMessage = (player_id: number) => {
  postMessage({ msg: POST_MESSAGES.CASHIER, msg_type: 'navigation', player_id });
};

const sendHomeMessage = () => {
  postMessage({ msg: POST_MESSAGES.CLOSE_GAME, msg_type: 'navigation' });
};
/* eslint-enable camelcase */

const doNavigation = ({ method, url, width, height, outerLink, queryString }: NavigationConfig) => {
  const _outerLink = isNil(outerLink) ? method === NAVIGATION_METHODS.REPLACE : outerLink;
  navigate(method, url, { width, height, outerLink: _outerLink, queryString });
};

const doLinkNavigation = <T extends string>(name: T) => (config: LinkNavigationResult): void => {
  const navigationParams: NavigationConfig = {
    method: config[`${name}Method`],
    url: config[`${name}Url`],
    width: config[`${name}PopupWidth`],
    height: config[`${name}PopupHeight`],
  };

  doNavigation(navigationParams);
};

type HomeNavigationConfig = {
  home: any;
  authParams: any;
  outerLink: boolean;
  tableId: number;
  identifier: string;
  [key: string]: any;
};

type CashierOpenPayload = {
  cashier?: CashierConfig;
  authParams?: Partial<AuthParams>;
  user: Partial<UserState>;
};

const doCashierNavigation = doLinkNavigation('cashier');
const doResponsibleGamingNavigation = doLinkNavigation('responsibleGaming');

const doHomeNavigation = ({
  homeMethod: method, homeUrl: url, outerLink, tableId, homeUseEzugiLobby = false, identifier,
}: HomeNavigationConfig) => {
  doNavigation(
    { method, url, outerLink, queryString: qs.stringify({ sourceTableId: tableId, identifier }) }
  );
  if (isPopup() && !homeUseEzugiLobby) window.close();
};

export const openCashier = ({
  cashier = {
    cashierMethod: 'popup',
  },
  authParams = {},
  user = {},
}: CashierOpenPayload) => new Promise((resolve, reject) => {
  const { postMessage, cashierUrl } = authParams;

  try {
    if (postMessage === '1' || cashier.cashierMethod === NAVIGATION_METHODS.POST) {
      const { uid = 0 } = user;
      sendCashierMessage(+uid);
    } else {
      const cashierConfig = { ...cashier, ...(cashierUrl && isUrl(cashierUrl) && { cashierUrl }) };
      doCashierNavigation(cashierConfig);
    }
    resolve(true);
  } catch (e) {
    reject(e);
  }
});

export const openResponsibleGaming = ({
  responsibleGaming = {},
  authParams = {},
}: any) => new Promise((resolve, reject) => {
  const { responsibleGamingUrl }: any = authParams;

  try {
    const responsibleGamingConfig = {
      ...responsibleGaming,
      ...(responsibleGamingUrl && isUrl(responsibleGamingUrl) && { responsibleGamingUrl }),
    };
    doResponsibleGamingNavigation(responsibleGamingConfig);
    resolve(true);
  } catch (e) {
    reject(e);
  }
});

export const goHome = ({
  home = {}, authParams = {}, outerLink, tableId, identifier, language,
}: HomeNavigationConfig) => new Promise((resolve, reject) => {
  home = camelizeKeys(home);
  const { postMessage, homeUrl } = authParams;
  const { homeUseEzugiLobby } = home;

  try {
    if (postMessage === '1' || home.homeMethod === NAVIGATION_METHODS.POST) {
      sendHomeMessage();
    } else {
      const homeConfig = {
        ...home,
        ...(homeUrl && isUrl(homeUrl) && { homeUrl }),
        outerLink,
        tableId,
        homeUseEzugiLobby,
        identifier,
        language,
      };
      doHomeNavigation(homeConfig);
    }
    resolve({ ok: true, method: home.homeMethod });
  } catch (e) {
    reject(e);
  }
});
